import { inject, Injectable } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { OtkList } from '../models/user-item-list';
import { CheckoutHistory } from '../models/checkout-history';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BroadcastService } from './broadcast.service';
import { PurchaseSuggestion } from '../models/purchase-suggestion';
import { Tutor } from '../models/tutor';
import { VisualizationHistory, VisualizationHistoryItem } from '../models/visualization-history';
import { UserStatsBundle } from '../models/statistics';
import { UserResponse } from '../models/user-interests';
import { LocaleService } from './locale.service';
import { Challenge, CreateChallengeData } from '../models/challenge';
import { RemoteLoginData } from '../models/client-configuration';
import { FollowedAuthor } from '../models/followed-author';
import { Actions, UserActions } from '../models/user-actions';
import { Util } from '../util';
import { TranslateService } from '@ngx-translate/core';
import { HistoryItem, HistoryUser } from '../models/history-user';
import { ConfigService } from './config.service';
import { BookClubCarousel } from '../models/bookclubcarousel';
import { HoldsFacade } from '../state/user/holds/holds.facade';
import { CheckoutsFacade } from '../state/user/checkouts/checkouts.facade';
import { FavoritesFacade } from '../state/user/favorites/favorites.facade';
import { EditListFacade } from '../edit-list/state/edit-list.facade';
import { ToastService } from '../_core/services/toast.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly toastService = inject(ToastService);
  private readonly editListFacade = inject(EditListFacade);

  isLoadingLists = true;
  checkoutHistory: CheckoutHistory[] = [];
  lists: OtkList[] = [];
  followedAuthors: FollowedAuthor[];
  visualizationHistory: VisualizationHistory = { elements: [] };
  purchaseSuggestions: PurchaseSuggestion[] = [];
  tutors: Tutor[];
  userActions: UserActions[] = [];
  bookClubsCollection: BookClubCarousel[];
  history: HistoryUser = { items: [], lastPage: true };

  private readonly checkoutsFacade = inject(CheckoutsFacade);
  private readonly holdsFacade = inject(HoldsFacade);
  private readonly paginatedFacade = inject(FavoritesFacade);

  constructor(
    private configService: ConfigService,
    private apiService: ApiService,
    private broadcastService: BroadcastService,
    private localeService: LocaleService,
    private authService: AuthService,
    private translate: TranslateService,
  ) { }

  public getUserData() {
    forkJoin([
      this.safeCall(this.holdsFacade.getHolds()),
      this.safeCall(this.checkoutsFacade.getCheckouts()),
    ]).subscribe();

    this.safeCall(this.paginatedFacade.getPaginatedFavorites()).subscribe();

    forkJoin([
      this.safeCall(this.apiService.getUserLists(this.getUserId())),
    ]).subscribe(([lists]) => {
      this.lists = lists;
      this.broadcastService.broadcastUserData(false);
      this.isLoadingLists = false;
    });
  }

  getUserLists() {
    this.apiService.getUserLists(this.getUserId()).subscribe((lists) => this.lists = lists);
  }

  get24SymbolsLoginUrl() {
    return this.apiService.get24SymbolsLoginUrl(this.getUserId());
  }

  getInvitations() {
    return this.apiService.getInvitations();
  }

  getUserListSubscriptions(limit: number, offset: number) {
    return this.apiService.getFollowedLists(this.getUserId(), limit, offset);
  }

  editList(list: OtkList) {
    this.editListFacade.openDialog(list);
  }

  followList(list: OtkList) {
    return this.apiService.followList(this.getUserId(), list.id).pipe(
      tap(() => {
        list.followedByActiveUser = true;
        list.followers++;
        this.toastService.showToast('lists.followed');
      })
    );
  }

  followListById(id: string) {
    return this.apiService.followList(this.getUserId(), Number(id));
  }

  getUserHistory(page: number, pageSize: number) {
    return this.apiService.getUserHistory(page, pageSize);
  }

  resetUserHistory() {
    this.history = { items: [], lastPage: true };
  }

  loadFollowedAuthors(): Observable<FollowedAuthor[]> {
    return this.apiService
      .getFollowedAuthorsForUser(this.getUserId())
      .pipe(tap((result) => (this.followedAuthors = result)));
  }

  followAuthor(author: string) {
    return this.apiService.followAuthor(this.getUserId(), author).pipe(
      tap((followedAuthor) => {
        this.addFollowedAuthorToCache(followedAuthor);
        this.toastService.showToast('lists.authorFollowed');
      })
    );
  }

  unfollowAuthor(author: string) {
    return this.apiService.unfollowAuthor(this.getUserId(), author).pipe(
      tap(() => {
        this.removeFollowedAuthorFromCache(author);
        this.broadcastService.broadcastAuthorUnfollowed(author);
        this.toastService.showToast('lists.authorUnfollowed');
      })
    );
  }

  private addFollowedAuthorToCache(followedAuthor: FollowedAuthor) {
    if (this.followedAuthors != null) {
      this.followedAuthors.push(followedAuthor);
      this.followedAuthors.sort((a, b) => (a.author > b.author ? 1 : -1));
    }
  }

  private removeFollowedAuthorFromCache(author: string) {
    if (this.followedAuthors != null) {
      const i = this.followedAuthors.findIndex((followedAuthor) => followedAuthor.author === author);
      this.followedAuthors.splice(i, 1);
    }
  }

  unfollowList(list: OtkList) {
    return this.apiService.unfollowList(this.getUserId(), list.id).pipe(
      tap(() => {
        this.broadcastService.broadcastListUnfollowed(list);
        this.toastService.showToast('lists.unfollowed');
        list.followedByActiveUser = false;
        list.followers--;
      })
    );
  }

  unfollowListById(id: string, title: string) {
    return this.apiService.unfollowList(this.getUserId(), Number(id));
  }

  removeRecordFromList(list: OtkList, recordId: string) {
    const itemId = list.items.find((item) => item.recordId === recordId).id;

    return this.apiService.removeFromList(this.getUserId(), list.id, itemId).pipe(
      tap((item) => {
        list.items = list.items.filter((_item) => _item.id !== item.id);
        this.lists.find((l) => l.id === Number(list.id)).items = list.items;
        this.toastService.showToast('messages.lists.removeItem');
      })
    );
  }

  getUserInterests() {
    return this.apiService.getUserInterests(this.getUserId());
  }

  removeCheckoutFromHistory(checkout: CheckoutHistory) {
    this.checkoutHistory = this.checkoutHistory.filter((_checkout) => _checkout.checkoutId !== checkout.checkoutId);
  }

  removeVisualizationFromHistory(visualization: VisualizationHistoryItem) {
    this.visualizationHistory.elements = this.visualizationHistory.elements.filter(
      (_visualization) => _visualization.id !== visualization.id
    );
  }

  removeItemFromHistory(historyItem: HistoryItem) {
    this.history.items = this.history.items.filter((_historyItem) => _historyItem.recordId !== historyItem.recordId);
  }

  loadPurchaseSuggestions(): Observable<PurchaseSuggestion[]> {
    if (!this.purchaseSuggestions.length) {
      return this.apiService
        .getUserPurchaseSuggestions(this.getUserId())
        .pipe(tap((result) => (this.purchaseSuggestions = result)));
    }

    return of(this.purchaseSuggestions);
  }

  removeSuggestion(suggestion: PurchaseSuggestion) {
    this.purchaseSuggestions = this.purchaseSuggestions.filter((_suggestion) => _suggestion.id !== suggestion.id);
  }

  loadTutors(): Observable<Tutor[]> {
    if (this.tutors == null) {
      return this.apiService.getTutorsEmails(this.getUserId()).pipe(tap((result) => (this.tutors = result)));
    }

    return of(this.tutors);
  }

  removeTutor(tutor: Tutor) {
    this.tutors = this.tutors.filter((_tutor) => _tutor.id !== tutor.id);
  }

  getListByRecordId(recordId: string): OtkList {
    return this.lists.find((list) => list.items.filter((item) => item.recordId === recordId).length > 0);
  }

  getUserId(): string {
    return this.authService.getLoggedUser()?.id ?? '';
  }

  getUserChallenges(): Observable<Challenge[]> {
    return this.apiService.getUserChallenges(this.getUserId());
  }

  getRemoteLoginUrl(): Observable<RemoteLoginData> {
    return this.apiService.getRemoteLoginUrl(this.getUserId());
  }

  getStatsBundle(): Observable<UserStatsBundle> {
    const userId = this.getUserId();
    return this.apiService.getUserStatsBundle(userId);
  }

  isInList(listId: number, recordId: string): boolean {
    const list = this.lists.find((_list) => _list.id === listId);
    return list.items.some((item) => item.recordId === recordId);
  }

  setUserInterests(responses: UserResponse[]): Observable<string> {
    return this.apiService.setUserInterests(this.getUserId(), responses).pipe(
      map((result: string) => {
        const user = this.authService.getLoggedUser();
        if (result) {
          user.policy = result;
        }
        this.authService.updateLoggedUser(user);
        this.updateInterestsAndNotify();
        return result;
      })
    );
  }

  getLibrary() {
    return this.authService.getLoggedUser().customer;
  }

  setDontShowInterests(): void {
    this.apiService.setDontShowInterests(this.getUserId()).subscribe(() => this.updateInterestsAndNotify());
  }

  resetUserData() {
    this.checkoutHistory = [];
    this.visualizationHistory.elements = [];
    this.purchaseSuggestions = [];
    this.tutors = [];
  }

  getChallenges() {
    return this.apiService.getUserChallenges(this.getUserId());
  }

  createChallenge(challenge: CreateChallengeData) {
    return this.apiService.createUserChallenge(this.getUserId(), challenge);
  }

  private safeCall<T>(func: Observable<T>): Observable<any[] | T> {
    return func.pipe(catchError(() => of([])));
  }

  private updateInterestsAndNotify(): void {
    const user = this.authService.getLoggedUser();
    user.interestsFilled = true;

    this.authService.updateLoggedUser(user);
    this.toastService.showToast('general.changesSaved');
  }

  verifyAccount(token: string) {
    return this.apiService.verifyAccount(token);
  }

  createGuestAccount(token: string) {
    return this.apiService.createGuestAccount(token);
  }

  loadBookClubs() {
    if (!this.bookClubsCollection) {
      const userId = this.getUserId();
      this.apiService.getBookClubs(userId).subscribe((bookClubsCollection) => {
        sessionStorage.setItem(Util.BOOKCLUBS_KEY, JSON.stringify(bookClubsCollection));
        this.bookClubsCollection = bookClubsCollection;
      });
    }
  }

  getOleConfiguration() {
    this.apiService.getOleConfiguration(this.getUserId()).subscribe((oleConfig) => {
      this.authService.getLoggedUser().canCreateUserGroups = oleConfig.canCreateUserGroups;
      this.userActions = [];
      if (oleConfig.canCreateExperiences && this.configService.get().showActionCreateExperience) {
        const actionCreateExperience = {} as UserActions;
        actionCreateExperience.buttonName = this.translate.instant('user.actions.createLearningExperience');
        actionCreateExperience.action = Actions.CREATE_BOOKCLUB;
        this.userActions.push(actionCreateExperience);
        this.broadcastService.broadcastSliderUserActions();
      }
    });
  }

  getIdToken(): string {
    return this.authService.getLoggedUser().idToken;
  }
}
